import { Schema, NodeSpec, MarkSpec } from 'prosemirror-model';
import { nodeSpecs as baseNodeSpecs, markSpecs as baseMarkSpecs } from '@kafka/shared/schema';

// Extend base specs with DOM-specific methods
const nodes: Record<string, NodeSpec> = {
  ...baseNodeSpecs,

  paragraph: {
    ...baseNodeSpecs.paragraph,
    parseDOM: [{ tag: 'p' }],
    toDOM() {
      return ['p', 0];
    },
  },

  heading: {
    ...baseNodeSpecs.heading,
    parseDOM: [
      { tag: 'h1', attrs: { level: 1 } },
      { tag: 'h2', attrs: { level: 2 } },
      { tag: 'h3', attrs: { level: 3 } },
    ],
    toDOM(node) {
      return ['h' + node.attrs.level, 0];
    },
  },

  task: {
    ...baseNodeSpecs.task,
    parseDOM: [
      {
        tag: 'div.task',
        contentElement: '.task-content',
        getAttrs(dom) {
          const element = dom as HTMLElement;
          return {
            done: element.classList.contains('done'),
            active: element.classList.contains('active'),
            id: element.dataset.id || '',
          };
        },
      },
    ],
    toDOM(node) {
      const classes = ['task'];
      if (node.attrs.done) classes.push('done');
      if (node.attrs.active) classes.push('active');

      const attrs: Record<string, string> = { class: classes.join(' ') };
      if (node.attrs.id) {
        attrs['data-id'] = node.attrs.id;
      }

      const children: unknown[] = [
        [
          'span',
          {
            class: `task-checkbox ${node.attrs.done ? 'done' : ''}`,
            contenteditable: 'false',
          },
        ],
        ['span', { class: 'task-content' }, 0],
      ];

      if (!node.attrs.done) {
        children.push([
          'span',
          { class: 'task-active-toggle', contenteditable: 'false' },
        ]);
      }

      return ['div', attrs, ...children];
    },
  },

  numberNode: {
    ...baseNodeSpecs.numberNode,
    toDOM(node) {
      return [
        'span',
        {
          class: 'number-node',
          'data-base': node.attrs.base,
        },
        String(node.attrs.base),
      ];
    },
    parseDOM: [
      {
        tag: 'span.number-node',
        getAttrs(dom) {
          const el = dom as HTMLElement;
          return {
            base: parseFloat(el.dataset.base || '0'),
          };
        },
      },
    ],
  },

  bulletList: {
    ...baseNodeSpecs.bulletList,
    parseDOM: [{ tag: 'ul' }],
    toDOM() {
      return ['ul', 0];
    },
  },

  orderedList: {
    ...baseNodeSpecs.orderedList,
    parseDOM: [
      {
        tag: 'ol',
        getAttrs(dom) {
          const element = dom as HTMLElement;
          return {
            order: element.hasAttribute('start')
              ? parseInt(element.getAttribute('start')!, 10)
              : 1,
          };
        },
      },
    ],
    toDOM(node) {
      return node.attrs.order === 1
        ? ['ol', 0]
        : ['ol', { start: node.attrs.order }, 0];
    },
  },

  listItem: {
    ...baseNodeSpecs.listItem,
    parseDOM: [{ tag: 'li' }],
    toDOM() {
      return ['li', 0];
    },
  },

  table: {
    ...baseNodeSpecs.table,
    parseDOM: [{ tag: 'table' }],
    toDOM() {
      return ['table', ['tbody', 0]];
    },
  },

  table_row: {
    ...baseNodeSpecs.table_row,
    parseDOM: [{ tag: 'tr' }],
    toDOM() {
      return ['tr', 0];
    },
  },

  table_cell: {
    ...baseNodeSpecs.table_cell,
    parseDOM: [
      {
        tag: 'td',
        getAttrs(dom) {
          const el = dom as HTMLElement;
          const widthAttr = el.getAttribute('data-colwidth');
          const colwidth = widthAttr ? widthAttr.split(',').map(w => parseInt(w, 10)) : null;
          return {
            colspan: parseInt(el.getAttribute('colspan') || '1', 10),
            rowspan: parseInt(el.getAttribute('rowspan') || '1', 10),
            colwidth,
          };
        },
      },
    ],
    toDOM(node) {
      const attrs: Record<string, string> = {};
      if (node.attrs.colspan !== 1) attrs.colspan = String(node.attrs.colspan);
      if (node.attrs.rowspan !== 1) attrs.rowspan = String(node.attrs.rowspan);
      if (node.attrs.colwidth) {
        attrs['data-colwidth'] = node.attrs.colwidth.join(',');
        attrs.style = `width: ${node.attrs.colwidth.reduce((a: number, b: number) => a + b, 0)}px`;
      }
      return ['td', attrs, 0];
    },
  },

  table_header: {
    ...baseNodeSpecs.table_header,
    parseDOM: [
      {
        tag: 'th',
        getAttrs(dom) {
          const el = dom as HTMLElement;
          const widthAttr = el.getAttribute('data-colwidth');
          const colwidth = widthAttr ? widthAttr.split(',').map(w => parseInt(w, 10)) : null;
          return {
            colspan: parseInt(el.getAttribute('colspan') || '1', 10),
            rowspan: parseInt(el.getAttribute('rowspan') || '1', 10),
            colwidth,
          };
        },
      },
    ],
    toDOM(node) {
      const attrs: Record<string, string> = {};
      if (node.attrs.colspan !== 1) attrs.colspan = String(node.attrs.colspan);
      if (node.attrs.rowspan !== 1) attrs.rowspan = String(node.attrs.rowspan);
      if (node.attrs.colwidth) {
        attrs['data-colwidth'] = node.attrs.colwidth.join(',');
        attrs.style = `width: ${node.attrs.colwidth.reduce((a: number, b: number) => a + b, 0)}px`;
      }
      return ['th', attrs, 0];
    },
  },
};

const marks: Record<string, MarkSpec> = {
  ...baseMarkSpecs,

  multiplier: {
    ...baseMarkSpecs.multiplier,
    parseDOM: [{ tag: 'span.multiplier' }],
    toDOM() {
      return ['span', { class: 'multiplier' }, 0];
    },
  },

  strong: {
    ...baseMarkSpecs.strong,
    parseDOM: [
      { tag: 'strong' },
      { tag: 'b' },
      {
        style: 'font-weight',
        getAttrs: (value) =>
          /^(bold(er)?|[5-9]\d{2})$/.test(value as string) && null,
      },
    ],
    toDOM() {
      return ['strong', 0];
    },
  },

  em: {
    ...baseMarkSpecs.em,
    parseDOM: [
      { tag: 'i' },
      { tag: 'em' },
      { style: 'font-style=italic' },
    ],
    toDOM() {
      return ['em', 0];
    },
  },

  tag: {
    ...baseMarkSpecs.tag,
    parseDOM: [{ tag: 'span.tag' }],
    toDOM() {
      return ['span', { class: 'tag' }, 0];
    },
  },
};

export const schema = new Schema({ nodes, marks });
